import { createApi } from '@reduxjs/toolkit/query/react';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';
// eslint-disable-next-line import/no-cycle
import AgentChatManager from '@src/logic/agentchatmanager';

export type GetChatsRequest = {
  type: 'ALL';
  merchantId: number;
  agent: number;
  businessProcess: number | null;
  contact: string;
  tags: string[];
  contactTagFilter: string[];
  dateRange: {
    to: string;
    from: string;
    sortingMode: 'CREATION_DATE';
  };
  sorting: [];
  limit: number;
  offset: number;
};

export type GetChatsCountRequest = Omit<GetChatsRequest, 'offset' | 'limit'>;

export type GetChatsResponse = {
  data: {
    id: string;
    provider: string;
    contactId: string;
    contact: string;
    agent: string;
    businessProcess: string;
    account: string;
    accountIconUrl: null | string;
    bot: string;
    botAccount: string;
    contactMsgAccount: string | null;
    messagingId: string | null;
    status: string;
    duration: string;
    tags: null | string[];
    openDate: number;
    creationDate: number;
  }[];
  paging: {
    hasMoreItems: true;
  };
};
export type GetChatsCountResponse = {
  count: number;
};

export type StartChatRequest = {
  botId: string | number;
  viaBotAccount: string | number;
  viaMsgProvider?: string | null;
  contactMsgAccount: string | number;
  contactId?: string | null;
  templatedMsgId?: string | null;
  fromNewTray?: boolean;
};

export type StartChatResponse = {
  chatId: string;
};

const chatsApi = createApi({
  reducerPath: 'chatsApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['chats'],
  endpoints: ({ query, mutation }) => ({
    getChats: query<GetChatsResponse, GetChatsRequest>({
      providesTags: ['chats'],
      serializeQueryArgs: args => args,
      keepUnusedDataFor: 0,
      query: request =>
        apiResource(B2ChatClient.resources.chats.actions.getChats, {
          data: request,
        }),
      extraOptions: {
        maxRetries: 1,
      },
    }),
    getChatsCount: query<GetChatsCountResponse, GetChatsCountRequest>({
      providesTags: ['chats'],
      keepUnusedDataFor: 0,
      query: data =>
        apiResource(B2ChatClient.resources.chats.actions.getChatsCount, {
          data,
        }),
      extraOptions: {
        maxRetries: 1,
      },
    }),
    /** Start a new chat with a contact via bot account (same contract as agentchatmanager.requestStartChat / backendproxy.startChatWithContact). */
    startChat: mutation<StartChatResponse, StartChatRequest>({
      invalidatesTags: ['chats'],
      transformResponse: (response: StartChatResponse) => {
        if (!response?.chatId || response?.chatId!.length < 5)
          throw new Error('startChat: missing chatId in response');
        return response;
      },
      query: ({
        botId,
        viaBotAccount,
        viaMsgProvider,
        contactMsgAccount,
        templatedMsgId,
        contactId,
        fromNewTray,
      }) => {
        const requestId = `${String(botId)}.${String(
          viaBotAccount
        )}.${Date.now()}`;
        const params: Record<string, string> = {
          'request-id': requestId,
          'bot-id': String(botId),
          'via-bot-account': String(viaBotAccount),
          'contact-account': String(contactMsgAccount),
          ...(viaMsgProvider && { 'via-msg-provider': String(viaMsgProvider) }),
          ...(contactId && { 'contact-id': String(contactId) }),
          ...(templatedMsgId && { 'templated-msg-id': String(templatedMsgId) }),
          ...(typeof fromNewTray === 'boolean' && {
            'from-new-tray': String(fromNewTray),
          }),
        };

        return apiResource(B2ChatClient.resources.agentChat.actions.startChat, {
          params,
        });
      },
      async onQueryStarted(_, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data?.chatId) {
          AgentChatManager.activeChatWS(data.chatId);
        }
      },
    }),
  }),
});

export const { useGetChatsQuery, useGetChatsCountQuery, useStartChatMutation } =
  chatsApi;

export default chatsApi;
